import clsx from 'clsx';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';

import { DateTimePicker } from '@/components/DateTimePicker';

import s from './DateNavPicker.module.scss';

// ─── helpers ────────────────────────────────────────────────────────────────

/** Add/subtract days from an ISO string (UTC-safe, mirrors DailyLog helpers). */
const shift = (iso: string | null | undefined, days: number): string => {
  const d = new Date(iso ?? new Date().toISOString());
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
};

/** Compare two dates at day-level only (ignores time). */
const isSameDayOrBefore = (a: Date, b: Date): boolean => {
  const ay = a.getUTCFullYear(),
    am = a.getUTCMonth(),
    ad = a.getUTCDate();
  const by = b.getUTCFullYear(),
    bm = b.getUTCMonth(),
    bd = b.getUTCDate();
  return ay < by || (ay === by && am < bm) || (ay === by && am === bm && ad <= bd);
};

// ─── main component ──────────────────────────────────────────────────────────

export type DateNavPickerProps = {
  /** ISO string. Defaults to today when absent. */
  value?: null | string;
  onChange: (iso: string) => void;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
};

export const DateNavPicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  className,
}: DateNavPickerProps) => {
  const currentDate: Date = new Date(value ?? new Date().toISOString());

  const isPrevDisabled = !!minDate && isSameDayOrBefore(currentDate, minDate);
  const isNextDisabled = !!maxDate && isSameDayOrBefore(maxDate, currentDate);

  const handlePrev = (): void => onChange(shift(value, -1));
  const handleNext = (): void => onChange(shift(value, +1));

  return (
    <div className={clsx(s.root, className)}>
      <button
        type="button"
        onClick={handlePrev}
        disabled={isPrevDisabled}
        className={s.navBtn}
        aria-label="Previous day"
      >
        <BiChevronLeft size={20} />
      </button>

      <DateTimePicker
        className={s.picker}
        placeholder="Jul 18, 2026"
        value={currentDate.toISOString()}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        showTimeInput={false}
      />

      <button
        type="button"
        onClick={handleNext}
        disabled={isNextDisabled}
        className={s.navBtn}
        aria-label="Next day"
      >
        <BiChevronRight size={20} />
      </button>
    </div>
  );
};
